import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// --- Cart item removal functionality ---
document.addEventListener('click', function (e) {
  // Check if the clicked element (or its parent) has the correct class
  const removeBtn = e.target.closest('.cart-items__name-input-remove');
  if (!removeBtn) return; // exit if click wasn't on a remove button

  e.preventDefault(); // prevent default button behavior

  // Find the parent wrapper (only affects the same group)
  const itemWrapper = removeBtn.closest('.cart-items__name-input-item');
  if (!itemWrapper) return;

  // Find the input inside this wrapper and clear it
  const input = itemWrapper.querySelector('.cart-items__name-input-field');
  if (input) input.value = '';
});

// --- Info popup positioning and interaction ---
(() => {
  let lockedWrapper = null;

  // --- Position popup relative to SVG icon ---
  function positionPopupToIcon(icon, popup) {
    const iconRect = icon.getBoundingClientRect();

    // Force visibility to measure correctly
    popup.style.visibility = 'hidden';
    popup.style.display = 'block';

    const popupWidth = popup.offsetWidth;

    popup.style.visibility = '';
    popup.style.display = '';

    let left = iconRect.left + iconRect.width / 2 - popupWidth / 2;
    let top = iconRect.bottom + 8;

    // Clamp to viewport
    if (left < 12) left = 12;
    if (left + popupWidth > window.innerWidth - 12) {
      left = window.innerWidth - popupWidth - 12;
    }

    popup.style.left = `${left}px`;
    popup.style.top = `${top}px`;
  }

  // --- Hover show ---
  document.addEventListener('pointerover', (e) => {
    const icon = e.target.closest('label svg');
    if (!icon) return;

    const wrapper = icon.closest('label');
    const popup = wrapper.querySelector('.cart__additional-test-info-popup');
    if (!popup) return;

    popup.classList.add('active');
    positionPopupToIcon(icon, popup);
  });

  // --- Hover leave ---
  document.addEventListener('pointerout', (e) => {
    const wrapper = e.target.closest('label');
    if (!wrapper) return;

    if (wrapper.contains(e.relatedTarget)) return;
    if (lockedWrapper === wrapper) return;

    const popup = wrapper.querySelector('.cart__additional-test-info-popup');
    popup?.classList.remove('active');
  });

  // --- Click toggle lock ---
  document.addEventListener('click', (e) => {
    const icon = e.target.closest('label svg');
    if (!icon) return;

    e.preventDefault();
    e.stopPropagation();

    const wrapper = icon.closest('label');
    const popup = wrapper.querySelector('.cart__additional-test-info-popup');
    if (!popup) return;

    const isSame = lockedWrapper === wrapper;

    // Close previous locked popup
    if (lockedWrapper && !isSame) {
      lockedWrapper.querySelector('.cart__additional-test-info-popup')?.classList.remove('active');
    }

    if (isSame) {
      popup.classList.remove('active');
      lockedWrapper = null;
    } else {
      popup.classList.add('active');
      positionPopupToIcon(icon, popup);
      lockedWrapper = wrapper;
    }
  });

  // --- Click outside to close ---
  document.addEventListener('click', (e) => {
    if (!lockedWrapper) return;
    if (lockedWrapper.contains(e.target)) return;

    lockedWrapper.querySelector('.cart__additional-test-info-popup')?.classList.remove('active');

    lockedWrapper = null;
  });

  // --- Close on scroll (important for position: fixed) ---
  window.addEventListener(
    'scroll',
    () => {
      if (!lockedWrapper) return;

      lockedWrapper.querySelector('.cart__additional-test-info-popup')?.classList.remove('active');

      lockedWrapper = null;
    },
    { passive: true }
  );
})();

function enforceDesktopOpen() {
  if (!window.matchMedia('(min-width: 750px)').matches) return;
  document.querySelectorAll('.cart-items__accordion:not([open])').forEach((el) => {
    el.open = true;
  });
}

window.addEventListener('resize', enforceDesktopOpen);
window.addEventListener('DOMContentLoaded', enforceDesktopOpen);

const cartItemsComponent = document.querySelector('cart-items-component');
if (cartItemsComponent) {
  let accordionFrame;
  new MutationObserver(() => {
    cancelAnimationFrame(accordionFrame);
    accordionFrame = requestAnimationFrame(enforceDesktopOpen);
  }).observe(cartItemsComponent, { childList: true, subtree: true });
}
